/* eslint-disable react-refresh/only-export-components -- the audio provider intentionally co-locates its consumer hook. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAudioPlayer } from '../hooks/useAudioPlayer.js';

export const AUDIO_STREAM_COMPLETED_EVENT = 'gaia:audio-stream-complete';

const AudioStreamContext = createContext(null);

/**
 * Synchronized audio streaming context
 *
 * Provides a single shared audio stream that all clients listen to simultaneously.
 * The DM controls when audio starts/stops, and all players hear the same thing at the same time.
 * Players can only mute/unmute their own audio.
 */
export const AudioStreamProvider = ({ children }) => {
  const { audioRef, isMuted, toggleMute, volumeLevel, setVolumeLevel } = useAudioPlayer();

  const [currentCampaignId, setCurrentCampaignId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);
  const [lastError, setLastError] = useState(null);
  const [currentChunkIds, setCurrentChunkIds] = useState([]);

  const clearPendingChunks = useCallback(() => {
    setCurrentChunkIds([]);
  }, []);

  const dispatchStreamComplete = useCallback(() => {
    if (
      typeof window === 'undefined' ||
      !currentCampaignId ||
      !currentChunkIds.length
    ) {
      return;
    }
    window.dispatchEvent(
      new CustomEvent(AUDIO_STREAM_COMPLETED_EVENT, {
        detail: {
          campaignId: currentCampaignId,
          chunkIds: [...currentChunkIds],
        },
      }),
    );
    clearPendingChunks();
  }, [currentCampaignId, currentChunkIds, clearPendingChunks]);

  // Stop streaming audio
  const stopStream = useCallback(() => {
    if (!audioRef.current) return;

    try {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current.load();
    } catch {
      // Ignore errors when stopping stream
    }

    setIsStreaming(false);
    setCurrentCampaignId(null);
    setLastError(null);
    clearPendingChunks();
  }, [audioRef, clearPendingChunks]);

  // Manually resume playback (for user gesture requirement)
  const resumePlayback = useCallback(async () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;

    // Don't interrupt if already playing - just clear the gesture requirement
    if (!audio.paused) {
      setNeedsUserGesture(false);
      setLastError(null);
      return;
    }

    // iOS Safari sometimes needs a load() call before play() to properly "unlock"
    // the audio element after a user gesture. This is especially true when the
    // source was set before the user gesture occurred.
    try {
      // Only reload if audio is paused and has a source - this unlocks iOS Safari
      if (audio.src && audio.paused) {
        audio.load();
      }

      await audio.play();
      setNeedsUserGesture(false);
      setLastError(null);
    } catch (error) {
      // On iOS Safari, if the source is stale or the audio context is locked,
      // playback cannot be resumed without a fresh stream.
      if (error.name === 'NotSupportedError' || error.name === 'NotAllowedError') {
        setLastError('Unable to start audio. Please try again.');
      } else {
        setLastError(error.message);
      }
    }
  }, [audioRef]);

  // Event listener for stream ended
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return undefined;
    }

    const handleEnded = async () => {
      dispatchStreamComplete();
      setIsStreaming(false);
      // Backend auto-advances via WebSocket - frontend just waits for audio_stream_started event
    };

    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioRef, dispatchStreamComplete, currentChunkIds, currentCampaignId]);

  const value = useMemo(() => ({
    // State
    currentCampaignId,
    isStreaming,
    isMuted,
    volumeLevel,
    needsUserGesture,
    lastError,
    pendingChunkCount: currentChunkIds.length,

    // Actions
    stopStream,
    resumePlayback,
    toggleMute,
    setVolumeLevel,
    clearPendingChunks,
  }), [
    currentCampaignId,
    isStreaming,
    isMuted,
    volumeLevel,
    needsUserGesture,
    lastError,
    currentChunkIds.length,
    stopStream,
    resumePlayback,
    toggleMute,
    setVolumeLevel,
    clearPendingChunks,
  ]);

  return (
    <AudioStreamContext.Provider value={value}>
      {children}
    </AudioStreamContext.Provider>
  );
};

export const useAudioStream = () => {
  const context = useContext(AudioStreamContext);
  if (!context) {
    throw new Error('useAudioStream must be used within AudioStreamProvider');
  }
  return context;
};

export default AudioStreamContext;
